import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { QuickStats } from '../components/dashboard/QuickStats';

interface DashboardPageProps {
  user: any;
  coachMode?: boolean;
  clientId?: number;
}

export const DashboardPage: React.FC<DashboardPageProps> = ({ user, coachMode, clientId }) => {

  // Today's data - use existing backend
  const today = new Date().toISOString().split('T')[0];
  const { data: todayData, isLoading: todayLoading } = useQuery({
    queryKey: coachMode ? ['client-today-meals', clientId, today] : ['today-meals', user?.id, today],
    queryFn: async () => {
      const url = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002'}/api/meals/portfolio?date=${today}${coachMode && clientId ? `&client_id=${clientId}` : ''}`;
      const response = await fetch(url, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      const data = await response.json();
      const meals = data.portfolio || [];
      
      return meals.reduce((acc: any, meal: any) => ({
        calories: acc.calories + (meal.totals?.calories || 0),
        protein: acc.protein + (meal.totals?.protein || 0),
        carbohydrates: acc.carbohydrates + (meal.totals?.carbs || 0),
        fat: acc.fat + (meal.totals?.fat || 0),
        meal_count: acc.meal_count + 1
      }), { calories: 0, protein: 0, carbohydrates: 0, fat: 0, meal_count: 0 });
    },
    enabled: coachMode ? !!clientId : !!user?.id,
  });

  // Yesterday's data - use existing backend
  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
  const { data: yesterdayData, isLoading: yesterdayLoading } = useQuery({
    queryKey: coachMode ? ['client-yesterday-meals', clientId, yesterday] : ['yesterday-meals', user?.id, yesterday],
    queryFn: async () => {
      const url = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002'}/api/meals/portfolio?date=${yesterday}${coachMode && clientId ? `&client_id=${clientId}` : ''}`;
      const response = await fetch(url, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      const data = await response.json();
      const meals = data.portfolio || [];
      
      return meals.reduce((acc: any, meal: any) => ({
        calories: acc.calories + (meal.totals?.calories || 0),
        protein: acc.protein + (meal.totals?.protein || 0),
        carbohydrates: acc.carbohydrates + (meal.totals?.carbs || 0),
        fat: acc.fat + (meal.totals?.fat || 0),
        meal_count: acc.meal_count + 1
      }), { calories: 0, protein: 0, carbohydrates: 0, fat: 0, meal_count: 0 });
    },
    enabled: coachMode ? !!clientId : !!user?.id,
  });

  // 7-day average - use existing backend but exclude today
  const { data: nutritionData, isLoading: nutritionLoading } = useQuery({
    queryKey: coachMode ? ['client-7day-meals', clientId] : ['7day-meals', user?.id],
    queryFn: async () => {
      const url = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002'}/api/meals/portfolio?days=8${coachMode && clientId ? `&client_id=${clientId}` : ''}`;
      const response = await fetch(url, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      const data = await response.json();
      const allMeals = data.portfolio || [];
      
      // Filter out today's meals to get only previous 7 complete days
      const today = new Date().toISOString().split('T')[0];
      const mealsExcludingToday = allMeals.filter((meal: any) => {
        const mealDate = new Date(meal.meal_date || meal.timestamp || meal.created_at).toISOString().split('T')[0];
        return mealDate !== today;
      });
      
      const totals = mealsExcludingToday.reduce((acc: any, meal: any) => ({
        calories: acc.calories + (meal.totals?.calories || 0),
        protein: acc.protein + (meal.totals?.protein || 0),
        carbohydrates: acc.carbohydrates + (meal.totals?.carbs || 0),
        fat: acc.fat + (meal.totals?.fat || 0),
        meal_count: acc.meal_count + 1
      }), { calories: 0, protein: 0, carbohydrates: 0, fat: 0, meal_count: 0 });

      return {
        meal_count: totals.meal_count,
        average_per_day: {
          calories: totals.calories / 7,
          protein: totals.protein / 7,
          carbohydrates: totals.carbohydrates / 7,
          fat: totals.fat / 7,
        }
      };
    },
    enabled: coachMode ? !!clientId : !!user?.id,
  });

  const goalsData = { goals: [] }; // Simplified


  if (nutritionLoading || todayLoading || yesterdayLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gray-200 rounded-lg h-32 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg p-6 shadow-sm">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-gray-200 rounded w-1/2" />
                <div className="h-8 bg-gray-200 rounded w-3/4" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-sm">
        <div className="px-6 py-8 sm:px-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-white">
                {coachMode ? 'Client:' : 'Dashboard:'} {user?.full_name || user?.fullName || user?.email || 'Unknown'} 👤
              </h1>
              <p className="mt-2 text-blue-100">
                Nutrition analysis overview for the past 7 days
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-white">
                {(nutritionData as any)?.meal_count || '0'}
              </div>
              <div className="text-blue-100 text-sm">
                meals logged
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <QuickStats 
        nutritionData={nutritionData} 
        goalsData={goalsData}
        todayData={todayData}
        yesterdayData={yesterdayData}
      />

      {/* Quick Navigation Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Link to="/nutrition-overview" className="card hover:shadow-lg transition-shadow cursor-pointer">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-blue-600">📊</span>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Nutrition Overview</h3>
                <p className="text-sm text-gray-500">Comprehensive 44-nutrient analysis</p>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/goals-progress" className="card hover:shadow-lg transition-shadow cursor-pointer">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-green-600">🎯</span>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Goals & Progress</h3>
                <p className="text-sm text-gray-500">Client goals and tracking</p>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/client-activity" className="card hover:shadow-lg transition-shadow cursor-pointer">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-green-600">👥</span>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Client Activity</h3>
                <p className="text-sm text-gray-500">Monitor client engagement and progress</p>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/reports" className="card hover:shadow-lg transition-shadow cursor-pointer">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                  <span className="text-orange-600">📧</span>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Reports</h3>
                <p className="text-sm text-gray-500">Email templates and insights</p>
              </div>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
};